// built with insights from https://www.digitalocean.com/community/tutorials/gatsbyjs-state-management-in-gatsby
import React, { useEffect, useState } from "react";
import {
  eraseCookie,
  getLocalStorage,
  setLocalStorage,
} from "@multiagentbook/ui";
import { onAuthStateChanged, Auth } from "firebase/auth";
import { auth } from "../components/auth/firebase/firebase";

interface IUser {
  displayName: string | null;
  email: string | null;
  emailVerified: boolean;
  isAnonymous: boolean;
  metadata: any;
  photoURL: string | null;
  userID: string;
}

export interface AppContextType {
  user: IUser | null;
  /**
   * False until Firebase has restored (or ruled out) a persisted session.
   * `user` is null in both the "signed out" and "not yet known" cases, so
   * without this a signed-in customer renders as anonymous on first paint.
   */
  authResolved: boolean;
  setUser: any;
  logout: any;
  cookie_name: string;
  darkMode: string;
  setDarkMode: any;
}

const cookie_name = "dml_app_cookie_";

export const appContext = React.createContext<AppContextType>(
  {} as AppContextType
);
const Provider = ({ children }: any) => {
  const [user, setUser] = useState<null | any>(null);
  const [authResolved, setAuthResolved] = useState(false);

  const [darkMode, setDarkMode] = useState(() => {
    const storedMode = getLocalStorage("darkmode", false);
    // Validate that the stored value is either "dark" or "light"
    if (storedMode === "dark" || storedMode === "light") {
      return storedMode;
    }
    // Default to "light" if no valid value is stored
    return "light";
  });

  const logout = React.useCallback(() => {
    setUser(null);
    eraseCookie(cookie_name);
    if (auth) {
      auth.signOut();
    }
  }, []);

  useEffect(() => {
    let unsubscribe: () => void = () => {};
    let refreshTimeout: NodeJS.Timeout;
    let lastRefresh = 0;
    const MIN_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

    // Smart refresh function that only refreshes when needed
    const refreshUserProfile = async () => {
      const now = Date.now();
      if (now - lastRefresh < MIN_REFRESH_INTERVAL) {
        return; // Skip if refreshed recently
      }

      try {
        if (auth?.currentUser) {
          await auth.currentUser.reload();
          lastRefresh = now;
          console.info("User profile refreshed successfully");
        }
      } catch (error) {
        console.warn("Failed to refresh user profile:", error);
      }
    };

    // Handle user activity to trigger smart refresh
    const handleUserActivity = () => {
      refreshUserProfile();
    };

    // Only set up auth listener if auth is available
    if (auth) {
      unsubscribe = onAuthStateChanged(auth, (user) => {
        setAuthResolved(true);
        if (user) {
          const {
            displayName,
            email,
            emailVerified,
            isAnonymous,
            photoURL,
            uid,
          } = user;
          const metadata = user;
          const currentUser: IUser = {
            displayName,
            email,
            emailVerified,
            isAnonymous,
            metadata,
            photoURL,
            userID: uid,
          };

          setUser(currentUser);

          // Set up activity-based refresh listeners
          document.addEventListener("visibilitychange", handleUserActivity);
          document.addEventListener("focus", handleUserActivity);

          // Set up a fallback refresh every hour (low frequency)
          refreshTimeout = setTimeout(() => {
            refreshUserProfile();
          }, 60 * 60 * 1000); // 1 hour fallback
        } else {
          setUser(null);
          // Clean up listeners when user logs out
          document.removeEventListener("visibilitychange", handleUserActivity);
          document.removeEventListener("focus", handleUserActivity);
          if (refreshTimeout) {
            clearTimeout(refreshTimeout);
          }
        }
      });
    } else {
      console.info("Firebase auth not initialized - running in demo mode");
      setAuthResolved(true);
    }

    // Cleanup function
    return () => {
      unsubscribe();
      document.removeEventListener("visibilitychange", handleUserActivity);
      document.removeEventListener("focus", handleUserActivity);
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, []);

  const updateDarkMode = React.useCallback((darkMode: string) => {
    setDarkMode(darkMode);
    setLocalStorage("darkmode", darkMode, false);
  }, []);

  const contextValue = React.useMemo(
    () => ({
      user,
      authResolved,
      setUser,
      logout,
      cookie_name,
      darkMode,
      setDarkMode: updateDarkMode,
    }),
    [user, authResolved, darkMode, logout, updateDarkMode]
  );

  return (
    <appContext.Provider value={contextValue}>{children}</appContext.Provider>
  );
};

// Export the Provider component directly for Vite/React Router
export default Provider;
